/*
 * Created Date: Thursday May 5th 2022
 * Author: Oskar Karnblad
 * License: Proprietary to Scaffcalc AB
 *
 * Copyright (c) 2022 Scaffcalc AB
 */
import { InfoOutlined } from "@mui/icons-material";
import {
  Select,
  MenuItem,
  InputLabel,
  Grid,
  FormControl,
  FormHelperText,
  Stack,
} from "@mui/material";

import { SyntheticEvent } from "react";
import ToolTip from "./ToolTip";

export interface DropDownProps {
  autoBlur?: boolean;
  backgroundColor?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  icon?: any;
  id?: string;
  infoButton?: boolean;
  onChange?: any;
  onClickInfo?: any;
  onFocus?: any;
  options?: any;
  placeholder?: string;
  required?: any;
  style?: any;
  value?: any;
  onClose?: any;
  ref?: any;
  tippyText?: string;
  skipNullCheck?: boolean;
}

const DropDown = ({
  autoBlur,
  disabled,
  icon,
  id,
  infoButton,
  onChange,
  onClickInfo,
  onFocus,
  options,
  placeholder,
  required,
  style,
  value,
  backgroundColor,
  error,
  helperText = "",
  onClose,
  ref,
  tippyText,
  skipNullCheck,
  ...other
}: DropDownProps) => {
  const isNullOrUndefined = (val: any) => {
    if (skipNullCheck) return false;

    return val === null || val === undefined;
  };

  return (
    <Grid
      container
      sx={{
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        ...style,
      }}
    >
      {icon && (
        <Grid item sx={{ display: "flex", marginRight: "4px" }}>
          {icon}
        </Grid>
      )}
      <Grid item sx={{ flex: 1, width: "100%" }}>
        <FormControl
          disabled={disabled}
          fullWidth
          margin="dense"
          size="small"
          required={required}
          error={error}
          sx={{
            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .Mui-error .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
        >
          {placeholder && (
            <ToolTip content={tippyText} placement="right">
              <InputLabel
                onClick={onClickInfo}
                sx={{
                  display: "flex",
                  alignContent: "center",
                  width: "100%",
                  userSelect: "none",
                  "&.Mui-focused": {
                    color: "text.secondary",
                  },
                }}
              >
                {placeholder}
                {(infoButton || tippyText) && !isNullOrUndefined(value) && (
                  <Stack
                    direction={"row"}
                    alignContent={"center"}
                    marginLeft={"8px"}
                    flexWrap={"wrap"}
                  >
                    <InfoOutlined
                      sx={{
                        fill: "text.secondary",
                        color: "text.secondary",
                        cursor: "pointer",
                        width: "20px",
                        height: "20px",
                      }}
                    />
                  </Stack>
                )}
              </InputLabel>
            </ToolTip>
          )}
          <Select
            ref={ref}
            id={id ? id : ""}
            onChange={onChange}
            defaultValue=""
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: "300px",
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    webkitAppearance: "none",
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    borderRadius: "2px",
                    backgroundColor: "text.icon",
                  },
                },
              },
            }}
            inputProps={{
              sx: {
                backgroundColor: "secondary.dark",
              },
            }}
            value={isNullOrUndefined(value) ? "" : value}
            onFocus={onFocus}
            onClose={(e: SyntheticEvent<Element, Event>) => {
              if (autoBlur) {
                setTimeout(() => {
                  const el: any = document.activeElement;
                  if (el) el.blur();
                }, 10);
              }

              if (onClose) onClose(e);
            }}
            {...other}
          >
            {options &&
              options.map((val: any, idx: number) => {
                return (
                  <MenuItem
                    key={idx}
                    value={isNullOrUndefined(val.value) ? "" : val.value}
                    style={{
                      display: val.hidden ? "none" : "auto",
                    }}
                    disabled={val.disabled}
                  >
                    {val.label}
                  </MenuItem>
                );
              })}
          </Select>
          {error && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
        {isNullOrUndefined(value) && onClickInfo && (
          <InfoOutlined
            onClick={onClickInfo}
            sx={{
              fill: "text.secondary",
              color: "text.secondary",
              cursor: "pointer",
              width: "20px",
              height: "20px",
              position: "absolute",
              right: "36px",
              top: "18px",
            }}
          />
        )}
      </Grid>
    </Grid>
  );
};

DropDown.defaultProps = {
  autoBlur: true,
};

export default DropDown;
